import React, { useState, ChangeEvent, FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { MultiValue } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { TagOption, fetchRawMaterialTags, createRawMaterialTag } from '../services/api';
import { customSelectStyles } from '../SelectStyles';

import { uploadFileToIPFS, uploadJsonToIPFS } from '../services/ipfsUploader';
import { getOrCreateSmartAccount, mintCertificateSponsored } from '../services/smartAccountService';
import { IPFS_GATEWAY_PREFIX } from '../constants';

import AlertModal from './AlertModal';
import CertificateQRCode from './CertificateQRCode';
import './MintCertificateForm.css';

interface CategoryOption {
    value: string;
    label: string;
}

interface TemplateData {
    name: string;
    description: string;
    categories: string[];
    rawMaterials: { value: string; label: string }[];
    vintageYear: string;
    imageUrl?: string;
    latitude?: number;
    longitude?: number;
}

interface MintCertificateFormProps {
    onMinted: (tokenId: string) => void;
    templateData?: TemplateData | null;
}

const categories = [
    "Textile arts (e.g. knitting, embroidery, quilting)",
    "Woodcrafts (e.g. carved objects, furniture)",
    "Ceramics & pottery",
    "Jewelry making",
    "Leatherwork",
    "Paper crafts (e.g. origami, scrapbooking)",
    "Glasswork (e.g. stained glass, blown glass)",
    "Traditional folk arts (e.g. Turkish ebru, Japanese urushi, Scandinavian rosemaling)",
    "Textile design",
    "Basket weaving",
    "Landscape Architecture and Biophilia Design",
    "Accessories",
    "Natural dyeing",
    "Indigo dyeing",
    "Weaving",
    "Tapestry",
    "Spinning",
    "Felting",
    "Fibre processing (linen, tow, wool, silk, etc.)",
    "Cultivation of dye plants",
    "Cultivation of plants for textile fibres",
    "Machine knitting",
    "Bookbinding",
    "Furniture"
];

const MAX_IMAGE_SIZE_MB = 1;
const MAX_TOTAL_SIZE_MB = 20;
const MAX_TEXT_LENGTH = 256;
const ACCEPTED_DOC_TYPES = '.pdf,.png,.jpg,.jpeg,.webp';

const MintCertificateForm: FC<MintCertificateFormProps> = ({ onMinted, templateData }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    const [selectedCategories, setSelectedCategories] = useState<readonly CategoryOption[]>([]);
    const [rawMaterialTags, setRawMaterialTags] = useState<readonly TagOption[]>([]);
    const [documentFiles, setDocumentFiles] = useState<File[]>([]);

    const [vintageYear, setVintageYear] = useState<string>('');
    const [locationError, setLocationError] = useState<string>('');

    const [isMinting, setIsMinting] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const { t } = useTranslation();

    const categoryOptions: CategoryOption[] = useMemo(() =>
        categories.map((cat, index) => ({
            value: cat,
            label: t(`categories.${index}`)
        })),
        [t]
    );

    const handleCategoryChange = (newValue: MultiValue<CategoryOption>) => {
        setSelectedCategories(newValue);
    };

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState<'success' | 'error'>('success');
    const [alertTxHash, setAlertTxHash] = useState<string | undefined>();
    const [mintedTokenId, setMintedTokenId] = useState<string | null>(null); // Track minted token for QR code
    const [pendingRedirectId, setPendingRedirectId] = useState<string | null>(null);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLatitude(position.coords.latitude);
                    setLongitude(position.coords.longitude);
                    setLocationError('');
                },
                (error) => {
                    console.error("Error getting location:", error);
                    setLocationError(`Error getting location: ${error.message}. Location will not be added to metadata.`);
                    setLatitude(null);
                    setLongitude(null);
                }
            );
        } else {
            setLocationError("Geolocation is not supported by this browser. Location will not be added to metadata.");
        }
    }, []);

    const [templateImageUrl, setTemplateImageUrl] = useState<string | null>(null);
    const [templateLocation, setTemplateLocation] = useState<{ lat: number; lng: number } | null>(null);

    useEffect(() => {
        if (!templateData) return;
        setName(templateData.name);
        setDescription(templateData.description);
        setVintageYear(templateData.vintageYear || '');
        setRawMaterialTags(templateData.rawMaterials);
        if (templateData.imageUrl) {
            setImagePreviewUrl(templateData.imageUrl);
            setTemplateImageUrl(templateData.imageUrl);
        }
        if (templateData.latitude != null && templateData.longitude != null) {
            setLatitude(templateData.latitude);
            setLongitude(templateData.longitude);
            setTemplateLocation({ lat: templateData.latitude, lng: templateData.longitude });
        }
        const matched = templateData.categories.map(catValue => {
            const idx = categories.indexOf(catValue);
            return { value: catValue, label: idx >= 0 ? t(`categories.${idx}`) : catValue };
        });
        setSelectedCategories(matched);
    }, [templateData, t]);

    const getTotalDocumentsSize = (files: File[]): number => {
        return files.reduce((sum, f) => sum + f.size, 0);
    };

    const handleDocumentsChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const newFiles = Array.from(e.target.files);
        const allFiles = [...documentFiles, ...newFiles];
        const totalSize = getTotalDocumentsSize(allFiles) + (imageFile?.size || 0);
        if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
            showAlert(t('errorTitle'), t('alert.totalSizeTooLarge', { maxSize: `${MAX_TOTAL_SIZE_MB}MB` }), 'error');
            e.target.value = '';
            return;
        }
        setDocumentFiles(allFiles);
        e.target.value = '';
    };

    const removeDocument = (index: number) => {
        setDocumentFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const MAX_FILE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

            if (file.size > MAX_FILE_SIZE_BYTES) {
                showAlert(t('errorTitle'), t('alert.fileTooLarge', { maxSize: `${MAX_IMAGE_SIZE_MB}MB` }), 'error');
                e.target.value = '';
                setImageFile(null);
                setImagePreviewUrl(null);
                return;
            }

            setImageFile(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreviewUrl(previewUrl);
        } else {
            setImageFile(null);
            setImagePreviewUrl(null);
        }
    };

    const showAlert = (title: string, message: string, type: 'success' | 'error', txHash?: string) => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertType(type);
        setAlertTxHash(txHash);
        setAlertOpen(true);
    };

    const handleRawMaterialChange = (newValue: MultiValue<TagOption>) => {
        setRawMaterialTags(newValue);
    };

    const handleCreateTag = async (inputValue: string) => {
        setIsCreating(true);
        try {
            const newOption = await createRawMaterialTag(inputValue);
            setRawMaterialTags([...rawMaterialTags, newOption]);
        } catch (err) {
            console.error("Failed to create tag:", err);
            showAlert(t('errorTitle'), t('alert.tagCreationError', { error: err instanceof Error ? err.message : 'Unknown error' }), 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value.length > MAX_TEXT_LENGTH) {
            showAlert(t('errorTitle'), t('alert.textTooLong', { maxLength: MAX_TEXT_LENGTH }), 'error');
        }
        // Always set the (possibly truncated) value
        setName(value.substring(0, MAX_TEXT_LENGTH));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
        if (value.length > MAX_TEXT_LENGTH) {
            showAlert(t('errorTitle'), t('alert.textTooLong', { maxLength: MAX_TEXT_LENGTH }), 'error');
        }
        // Always set the (possibly truncated) value
        setDescription(value.substring(0, MAX_TEXT_LENGTH));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!name || !description || (!imageFile && !templateImageUrl) || selectedCategories.length === 0) {
            showAlert(t('errorTitle'), t('alert.validationErrorMessage'), 'error');
            return;
        }
        setIsMinting(true);

        try {
            // Ensure user has a smart account
            await getOrCreateSmartAccount();

            let imageUri: string;
            if (imageFile) {
                const imageIpfsHash = await uploadFileToIPFS(imageFile);
                imageUri = `${IPFS_GATEWAY_PREFIX}${imageIpfsHash}`;
            } else if (templateImageUrl) {
                imageUri = templateImageUrl;
            } else {
                throw new Error('Image is required');
            }

            const documentHashes: string[] = [];
            for (const doc of documentFiles) {
                const hash = await uploadFileToIPFS(doc);
                documentHashes.push(hash);
            }

            const metadata = {
                name: name,
                description: description,
                image: imageUri,
                attributes: [
                    ...selectedCategories.map(c => ({ trait_type: "Category", value: c.value })),
                    ...rawMaterialTags.map(tag => ({
                        trait_type: "Raw Material",
                        value: tag.label
                    })),
                    ...(vintageYear ? [{ trait_type: "Vintage Year", value: vintageYear }] : []),
                    ...(latitude && longitude ? [{ trait_type: "Location", value: `${latitude.toFixed(5)}, ${longitude.toFixed(5)}` }] : []),
                    ...documentHashes.map((hash, i) => ({
                        trait_type: "Document",
                        value: `${IPFS_GATEWAY_PREFIX}${hash}`,
                        name: documentFiles[i].name
                    }))
                ]
            };

            // Upload metadata to IPFS
            const metadataIpfsHash = await uploadJsonToIPFS(metadata);
            const tokenMetadataURI = `ipfs://${metadataIpfsHash}`;

            // Mint via backend (sponsored transaction)
            const imageHash = imageUri.replace(IPFS_GATEWAY_PREFIX, '');
            const result = await mintCertificateSponsored(
                'craftsman',
                name,
                description,
                imageHash,
                tokenMetadataURI,
                { categories: selectedCategories.map(c => c.value), rawMaterials: rawMaterialTags.map(t => t.label), vintageYear }
            );

            if (!result.success) {
                throw new Error(result.error || 'Minting failed');
            }

            showAlert(
                t('successTitle'),
                result.tokenId
                    ? t('mintSuccessAlert', { tokenId: result.tokenId })
                    : t('mintSuccessAlertPending', 'Certificate minting submitted! Check your transactions for status.'),
                'success',
                result.transactionHash
            );

            if (result.tokenId) {
                setMintedTokenId(result.tokenId);
                setPendingRedirectId(result.tokenId);
            }

            setName('');
            setDescription('');
            setImageFile(null);
            setSelectedCategories([]);
            setRawMaterialTags([]);
            setVintageYear('');
            setImagePreviewUrl(null);
            setTemplateImageUrl(null);
            setDocumentFiles([]);
            (e.target as HTMLFormElement).reset();
        } catch (err) {
            console.error("Minting failed:", err);
            let errorMessage = 'Failed to create certificate.';
            if (err instanceof Error) {
                errorMessage = err.message;
            }
            showAlert(t('updateFailedTitle'), t('alert.mintingFailed', { errorMessage }), 'error');
        } finally {
            setIsMinting(false);
        }
    };

    return (
        <div className="mint-form-container">
            <h3>{t('issueNewCertificateTitle')}</h3>
            {locationError && <p className="error-message">{locationError}</p>}
            {templateLocation && latitude != null && longitude != null && (
                <div className="template-location-banner">
                    <span>
                        {t('form.templateLocationLabel', 'Using location from template:')} {latitude.toFixed(5)}, {longitude.toFixed(5)}
                    </span>
                    <button type="button" className="remove-doc-button" onClick={() => {
                        setTemplateLocation(null);
                        setLatitude(null);
                        setLongitude(null);
                    }}>&times;</button>
                </div>
            )}

            {imagePreviewUrl && name && description && selectedCategories.length > 0 && (
                <div className="certificate-preview-window">
                    <h4>Certificate Preview</h4>
                    {imagePreviewUrl && <img src={imagePreviewUrl} alt="Preview" className="image-preview" />}
                    <p><strong>Name:</strong> {name || "Not set"}</p>
                    {/* <p><strong>Raw Materials:</strong> {rawMaterials || "Not set"}</p> */}
                    <p><strong>{t('form.nameLabel')}:</strong> {name || "Not set"}</p>
                    <p><strong>{t('form.categoryLabel')}:</strong> {selectedCategories.map(c => c.label).join(', ') || "Not set"}</p>
                    <p><strong>{t('form.rawMaterialsLabel')}:</strong> {rawMaterialTags.map(t => t.label).join(', ') || "Not set"}</p>

                    <p><strong>Vintage Year:</strong> {vintageYear || "Not set"}</p>
                    <p><strong>Description:</strong> {description || "Not set"}</p>
                    {latitude && longitude && (
                        <p>
                            <strong>Location:</strong> {latitude.toFixed(5)}, {longitude.toFixed(5)}
                            <a href={`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=15/${latitude}/${longitude}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '10px' }}>
                                View on Map
                            </a>
                        </p>
                    )}
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <div>
                    <label htmlFor="cert-name">{t('form.nameLabel')}:</label>
                    <input type="text" id="cert-name" value={name} onChange={handleNameChange} required />
                    <div className="char-counter">{name.length} / {MAX_TEXT_LENGTH}</div>
                </div>
                <div>
                    <label htmlFor="cert-desc">{t('form.descriptionLabel')}:</label>
                    <textarea id="cert-desc" value={description} onChange={handleDescriptionChange} required placeholder={t('form.descriptionPlaceholder')} />
                    <div className="char-counter">{description.length} / {MAX_TEXT_LENGTH}</div>
                </div>
                <div>
                    <label htmlFor="cert-category">{t('form.categoryLabel')}:</label>
                    <Select<CategoryOption, true>
                        inputId="cert-category"
                        isMulti
                        isClearable
                        options={categoryOptions}
                        value={selectedCategories}
                        onChange={handleCategoryChange}
                        styles={customSelectStyles as any}
                        placeholder={t('form.categoryPlaceholder', 'Select categories...')}
                        isDisabled={isMinting}
                    />
                </div>
                <div>
                    <label htmlFor="cert-raw-materials">{t('form.rawMaterialsLabel')}:</label>
                    {/* <input type="text" id="cert-raw-materials" value={rawMaterials} onChange={(e) => setRawMaterials(e.target.value)} placeholder='(e.g. raw wool, clay, wood, natural dyeing plant, silk)'/> */}
                    <AsyncCreatableSelect
                        id="cert-raw-materials"
                        isMulti
                        isClearable
                        isDisabled={isCreating || isMinting}
                        isLoading={isCreating}
                        onChange={handleRawMaterialChange}
                        onCreateOption={handleCreateTag}
                        formatCreateLabel={(inputValue) => (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>+</span>
                                {`${t('createTagLabel', 'Create')} "${inputValue}"`}
                            </div>
                        )}
                        getNewOptionData={(inputValue, optionLabel) => ({
                            value: inputValue.toLowerCase().replace(/\W/g, ''), // The unique value for the tag
                            label: inputValue, // The display label for the tag data

                            __isNew__: true
                        } as TagOption)}
                        loadOptions={fetchRawMaterialTags}
                        placeholder={t('form.rawMaterialsPlaceholder', 'Select or create materials...')}

                        // placeholder={t('rawMaterialsPlaceholder') || '(e.g. raw wool, clay, wood, natural dyeing plant, silk)'}
                        value={rawMaterialTags}
                    />
                </div>
                <div>
                    <label htmlFor="cert-vintage-year">{t('form.vintageYearLabel')}:</label>
                    <input type="number" id="cert-vintage-year" value={vintageYear} onChange={(e) => setVintageYear(e.target.value)} placeholder="e.g., 1985" min="1000" max={new Date().getFullYear().toString()} />
                </div>
                <div>
                    <label htmlFor="cert-image">{t('form.imageLabel')}:</label>
                    <input type="file" id="cert-image" className="cert-image-input" accept="image/*" onChange={handleImageChange} required={!templateImageUrl} />
                </div>
                <div>
                    <label htmlFor="cert-documents">{t('form.documentsLabel')}:</label>
                    <p className="field-description">{t('form.documentsDescription', { maxSize: MAX_TOTAL_SIZE_MB })}</p>
                    <input type="file" id="cert-documents" className="cert-image-input" accept={ACCEPTED_DOC_TYPES} multiple onChange={handleDocumentsChange} />
                    {documentFiles.length > 0 && (
                        <ul className="document-list">
                            {documentFiles.map((file, index) => (
                                <li key={index}>
                                    <span>{file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                    <button type="button" className="remove-doc-button" onClick={() => removeDocument(index)}>&times;</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <button type="submit" disabled={isMinting}>
                    {isMinting ? t('form.mintingButton') : t('form.mintCertificateButton')}
                </button>
            </form>
            {/* {message && <p className={message.startsWith("Error") ? "error-message" : "success-message"}>{message}</p>} */}
            <AlertModal
                isOpen={alertOpen}
                onClose={() => {
                    setAlertOpen(false);
                    if (pendingRedirectId) {
                        onMinted(pendingRedirectId);
                    }
                }}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                txHash={alertTxHash}
            />
            {/* Show QR Code after successful minting */}
            {mintedTokenId && (
                <div className="minted-qr-section">
                    <CertificateQRCode tokenId={mintedTokenId} />
                    <button
                        onClick={() => setMintedTokenId(null)}
                        className="close-qr-button"
                        style={{ marginTop: '10px', backgroundColor: '#6c757d' }}
                    >
                        {t('qrCode.closeButton', 'Close QR Code')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default MintCertificateForm;

/* Add to App.css or a new CSS file */
/*
.mint-form-container { margin-bottom: 30px; padding: 20px; border: 1px solid #ccc; border-radius: 5px; }
.mint-form-container div { margin-bottom: 10px; }
.mint-form-container label { display: block; margin-bottom: 5px; }
.mint-form-container input[type="text"], .mint-form-container textarea, .mint-form-container input[type="file"] { width: calc(100% - 22px); padding: 10px; border: 1px solid #ddd; border-radius: 4px; }
.mint-form-container button { padding: 10px 20px; background-color: #28a745; color: white; border: none; border-radius: 5px; cursor: pointer; }
.mint-form-container button:disabled { background-color: #aaa; }
.error-message { color: red; margin-top:10px; }
.success-message { color: green; margin-top:10px; }
*/